import { useEffect, useRef, useState } from "react";

import { CommonTableHeaderComponent } from "@/Common/Components/Tables/CommonTableHeaderComponent";
import { MyleTable } from "@/Common/Components/Tables/MyleTable";
import { MyleTableColumnOrderControlv2 } from "@/Common/Components/Tables/MyleTableColumnOrderControlv2";
import { SimplePagerControl } from "@/Common/Components/Tables/SimplePagerControl";
import { useOnStoreChange } from "@/Common/CustomHooks/useOnStoreChange";
import { useTranslation } from "@/Common/CustomHooks/useTranslation";
import { EnvironmentTax } from "@/Common/Helpers/EnvironmentTax";
import { TableDataSourceItem } from "@/Common/Models/UI/Tables/ITableDataSourceItem";
import { PayableInboxItem } from "../Api/Inbox/Dto/Response/BillingInboxItem";
import { PayableInboxTableHelper } from "../Helpers/PayableInboxTableHelper";
import { PayableInboxActions } from "../Stores/Actions/PayableInboxActions";
import { PayableInboxSettingsContainer } from "./PayableInboxSettingsContainer";

import styles from "./PayableInbox.module.scss";

function PayableInbox(): JSX.Element {
    const { getText } = useTranslation();
    const helper = useRef(new PayableInboxTableHelper());
    const [showFilterSettings, setShowFilterSettings] = useState(false);
    const [isColumnDisplayPopupVisible, setColumnDisplayPopupIsVisible] = useState<boolean>(false);

    const [payableInboxModel, setPayableInboxModel] = useState(billingStore.payableInboxModel);
    const { data, sums, filterSettings } = billingStore.payableInboxModel;

    const tableItems: TableDataSourceItem<PayableInboxItem>[] =
        data?.map(p => new TableDataSourceItem(p)) ?? [];

    useEffect(() => {
        $(window).trigger("resize");

        PayableInboxActions.loadPayableInboxItems(payableInboxModel.filterSettings);
    }, []);

    useOnStoreChange(billingStore, () => onDataChanged());

    const onDataChanged = (): void => setPayableInboxModel(billingStore.payableInboxModel);

    const OnPrintReport = async (): Promise<void> => {
        const columnsToShow = helper.current.getTableColumns()
            .filter(p => p.columnOptions?.IsHidden === false)
            .map(p => p.columnOptions?.FieldName).toArray();

        await billingStore.BillingAction.PrintPayablesReport(billingStore.payableInboxModel, columnsToShow);
    };

    const onSearch = (text: string): void => {
        const nextFilterSettings = {
            ...filterSettings,
            SearchText: text
        };

        PayableInboxActions.loadPayableInboxItems(nextFilterSettings);
    };

    const shouldShowGst = (): boolean => sums?.GSTSum > 0
            || EnvironmentTax.hasGst();

    const shouldShowPstOrQst = (): boolean => sums?.QSTSum > 0
            || EnvironmentTax.hasPstOrQst();

    const shouldShowHst = (): boolean => sums?.HSTSum > 0
            || EnvironmentTax.hasHst();

    const shouldShowGenericTax = (): boolean => sums?.GenericTaxSum > 0
            || EnvironmentTax.hasGenericTax();

    return (
        <div className={`workspace ${styles.payableInbox}`}>
            <CommonTableHeaderComponent
                title={getText("ReceivablesLabel")}
                searchText={billingStore.payableInboxModel.filterSettings.SearchText}
                searchPlaceholder={getText("SearchLabel")}
                onSearch={text => onSearch(text)}
                onRefreshClick={() => PayableInboxActions.loadPayableInboxItems(billingStore.payableInboxModel.filterSettings)}
                onSettingsClick={() => setShowFilterSettings(!showFilterSettings)}
                onPrintClick={async () => await OnPrintReport()}
            />
            <div id="posref" className="tb ws-scrollable">
                {showFilterSettings && (
                    <PayableInboxSettingsContainer
                        onClose={() => setShowFilterSettings(false)}
                        filterSettings={filterSettings}
                        onColumnOrderingPopupOpenClick={() => {
                            setShowFilterSettings(false);
                            setColumnDisplayPopupIsVisible(true);
                        }}
                    />
                )}

                {isColumnDisplayPopupVisible && (
                    <MyleTableColumnOrderControlv2
                        TableColumns={helper.current.getColumnOptionsForOrderColumnsPopup()}
                        SaveOptionskeyTarget={helper.current.saveOptionKey}
                        ContainerId="payableInboxColumnsSettingsPopup"
                        closeColumnOrderPopup={() => {
                            helper.current.updateColumnsOption();
                            setColumnDisplayPopupIsVisible(false);
                        }}
                    />
                )}

                <div className="myle-doc-tb main-content table-wrapper">
                    <MyleTable
                        TableRenderOptions={helper.current.getTableRenderOptions()}
                        TableId="billing_payable_inbox"
                        Columns={helper.current.getTableColumns()}
                        DataSource={tableItems.toImmutable()}
                        SaveOptionskeyTarget={helper.current.saveOptionKey}
                        TableDataCy="billingInbox-table"
                    />
                </div>
            </div>
            <div className="ws-footer table-pager-footer">
                <div className="row">
                    <div className="col-lg-4 col-md-4 col-sm-4">
                        <SimplePagerControl
                            PageSize={filterSettings.Pagination.PageSize}
                            TotalRows={filterSettings.Pagination.TotalCount}
                            CurrentPage={Math.floor(filterSettings.Pagination.Offset / filterSettings.Pagination.PageSize) + 1}
                            changePageFunction={(nextPageNumber: number) => { PayableInboxActions.changePage(nextPageNumber); }}
                        />
                    </div>
                    <div className="col-lg-8 col-md-8 col-sm-8 text-right">
                        <p className={styles.footerText}>
                            <strong>{cacheStore.GetString("SubtotalLabel")}: ${sums?.SubtotalSum} </strong>
                            <strong>{cacheStore.GetString("DiscountLabel")}: ${sums?.DiscountSum} </strong>
                            {shouldShowGst() && <strong> {cacheStore.GetString("GstMLabel")}: ${sums?.GSTSum} </strong>}
                            {shouldShowPstOrQst() && <strong> {EnvironmentTax.getPstOrQstLabel()}: ${sums?.QSTSum} </strong>}
                            {shouldShowHst() && <strong>{cacheStore.GetString("HstMLabel")}: ${sums?.HSTSum} </strong>}
                            {shouldShowGenericTax() && <strong>{cacheStore.GetString("TaxLabel")}: ${sums?.GenericTaxSum} </strong>}
                            <strong>{cacheStore.GetString("TotalLabel")}: ${sums?.TotalBillSum}</strong>
                        </p>
                    </div>
                </div>
            </div>
        </div>
    );
}

export { PayableInbox };
